/**
 * useLCMutations Hook
 *
 * Provides mutations for liquid culture CRUD operations.
 * Handles timeline event generation on LC creation and status changes.
 */

import { useState, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toLocalDateString } from '../utils/dateUtils'
import { toast } from 'sonner'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

/**
 * Hook providing mutation operations for liquid cultures
 * @returns {Object} { createLC, updateLC, updateStatus, loading, error }
 */
export function useLCMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Generate a unique culture code using the database function
   * @param {string} strainCode - The strain code (e.g., 'SHI')
   * @param {number} generation - The generation number (1, 2, or 3)
   * @returns {Promise<string>} The generated culture code
   */
  const generateCultureCode = useCallback(async (strainCode, generation) => {
    try {
      const { data, error: rpcError } = await supabase.rpc('generate_lc_code', {
        strain_code: strainCode,
        gen: generation
      })

      if (rpcError) {
        throw rpcError
      }

      return data
    } catch (err) {
      console.error('[generateCultureCode] Error:', err)
      // Fallback to client-side generation
      const year = new Date().getFullYear()
      const timestamp = Date.now().toString().slice(-4)
      return `LC-${strainCode}-G${generation}-${year}-${timestamp}`
    }
  }, [])

  /**
   * Create timeline events for a newly created liquid culture
   * @param {Object} lc - The created liquid culture record
   */
  const createTimelineEvents = useCallback(async (lc) => {
    try {
      const now = toLocalDateString(new Date())
      const readyDate = lc.ready_date

      // Create events for LC lifecycle
      const events = [
        {
          entity_type: 'liquid_culture',
          entity_id: lc.id,
          event_type: 'inoculation',
          event_description: `LC ${lc.culture_code} inoculated`,
          planned_date: lc.inoculation_date,
          actual_date: lc.inoculation_date,
          status: 'completed',
          is_task: false
        },
        {
          entity_type: 'liquid_culture',
          entity_id: lc.id,
          event_type: 'colonisation_complete',
          event_description: `LC ${lc.culture_code} ready for use`,
          planned_date: readyDate,
          actual_date: null,
          status: now >= readyDate ? 'pending' : 'planned',
          is_task: true,
          task_description: `Check LC ${lc.culture_code} - colonisation should be complete`
        }
      ]

      const { error: insertError } = await supabase
        .from('timeline_events')
        .insert(events)

      if (insertError) {
        console.error('[createTimelineEvents] Error:', insertError)
      }
    } catch (err) {
      console.error('[createTimelineEvents] Error:', err)
    }
  }, [])

  /**
   * Deduct volume from a culture syringe using the edge function
   * @param {string} syringeId - The syringe UUID
   * @param {number} volumeUsed - Volume used in ml
   * @returns {Promise<Object>} Updated syringe data
   */
  const useSyringeVolume = useCallback(async (syringeId, volumeUsed) => {
    const session = await supabase.auth.getSession()
    const accessToken = session?.data?.session?.access_token

    const response = await fetch(`${SUPABASE_URL}/functions/v1/culture-syringes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        action: 'use',
        syringeId,
        volumeUsed
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to deduct syringe volume')
    }

    return response.json()
  }, [])

  /**
   * Create a new liquid culture
   * @param {Object} lcData - The liquid culture data
   * @param {string} lcData.strain_id - Strain UUID
   * @param {number} lcData.generation - Generation (1, 2, or 3)
   * @param {number} lcData.volume_ml - Volume in millilitres
   * @param {string} lcData.inoculation_date - ISO date string
   * @param {string} [lcData.parent_culture_id] - Parent LC UUID
   * @param {string} [lcData.source_syringe_id] - Source syringe UUID (for third-party syringes)
   * @param {number} [lcData.volume_used_ml] - Volume used from syringe in ml
   * @param {string} [lcData.storage_location] - Storage location
   * @param {string} [lcData.notes] - Notes
   * @returns {Promise<Object>} The created liquid culture
   */
  const createLC = useCallback(async (lcData) => {
    setLoading(true)
    setError(null)

    try {
      // Get strain details for code generation
      const { data: strain, error: strainError } = await supabase
        .from('strains')
        .select('code')
        .eq('id', lcData.strain_id)
        .single()

      if (strainError) {
        throw new Error('Failed to fetch strain details')
      }

      // Generate culture code
      const cultureCode = await generateCultureCode(strain.code, lcData.generation)

      // Build insert payload
      const insertPayload = {
        culture_code: cultureCode,
        strain_id: lcData.strain_id,
        generation: lcData.generation,
        volume_ml: lcData.volume_ml,
        inoculation_date: lcData.inoculation_date,
        storage_location: lcData.storage_location || null,
        notes: lcData.notes || null,
        status: 'incubating'
      }

      // Set source - either parent culture or syringe
      if (lcData.source_syringe_id) {
        insertPayload.source_syringe_id = lcData.source_syringe_id
        insertPayload.parent_culture_id = null
      } else {
        insertPayload.parent_culture_id = lcData.parent_culture_id || null
        insertPayload.source_syringe_id = null
      }

      // Create the liquid culture
      const { data, error: insertError } = await supabase
        .from('liquid_cultures')
        .insert(insertPayload)
        .select(`
          *,
          strain:strains(id, code, name, origin)
        `)
        .single()

      if (insertError) {
        throw insertError
      }

      // If created from syringe, deduct the volume used
      if (lcData.source_syringe_id && lcData.volume_used_ml) {
        try {
          await useSyringeVolume(lcData.source_syringe_id, lcData.volume_used_ml)
        } catch (syringeErr) {
          console.error('[createLC] Syringe volume deduction error:', syringeErr)
          // LC was created but syringe deduction failed - warn but don't fail
          toast.warning('LC created but syringe volume update failed', {
            description: 'Please manually update the syringe volume'
          })
        }
      }

      // Create timeline events for the new LC
      await createTimelineEvents(data)

      const sourceLabel = lcData.source_syringe_id ? 'from syringe' : ''
      toast.success(`Created ${cultureCode}`, {
        description: `${data.strain.name} G${data.generation} liquid culture ${sourceLabel}`.trim()
      })

      return data
    } catch (err) {
      console.error('[createLC] Error:', err)
      setError(err.message)
      toast.error('Failed to create liquid culture', {
        description: err.message
      })
      throw err
    } finally {
      setLoading(false)
    }
  }, [generateCultureCode, createTimelineEvents, useSyringeVolume])

  /**
   * Update an existing liquid culture
   * @param {string} id - The liquid culture UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} The updated liquid culture
   */
  const updateLC = useCallback(async (id, updates) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: updateError } = await supabase
        .from('liquid_cultures')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select(`
          *,
          strain:strains(id, code, name, origin)
        `)
        .single()

      if (updateError) {
        throw updateError
      }

      toast.success(`Updated ${data.culture_code}`)

      return data
    } catch (err) {
      console.error('[updateLC] Error:', err)
      setError(err.message)
      toast.error('Failed to update liquid culture', {
        description: err.message
      })
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update liquid culture status with timeline event logging
   * @param {string} id - The liquid culture UUID
   * @param {string} newStatus - The new status
   * @param {string} [notes] - Optional notes for the status change
   * @returns {Promise<Object>} The updated liquid culture
   */
  const updateStatus = useCallback(async (id, newStatus, notes = null) => {
    setLoading(true)
    setError(null)

    try {
      // Fetch current LC to get culture_code
      const { data: currentLC, error: fetchError } = await supabase
        .from('liquid_cultures')
        .select('culture_code, status')
        .eq('id', id)
        .single()

      if (fetchError) {
        throw fetchError
      }

      const previousStatus = currentLC.status

      // Update the status
      const { data, error: updateError } = await supabase
        .from('liquid_cultures')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select(`
          *,
          strain:strains(id, code, name, origin)
        `)
        .single()

      if (updateError) {
        throw updateError
      }

      // Create a timeline event for the status change
      const eventDescriptions = {
        incubating: 'LC returned to incubation',
        ready: 'LC colonisation complete - ready for use',
        in_use: 'LC being used for spawn production',
        finished: 'LC fully depleted',
        contaminated: 'LC contaminated and discarded'
      }

      await supabase.from('timeline_events').insert({
        entity_type: 'liquid_culture',
        entity_id: id,
        event_type: 'state_change',
        event_description: eventDescriptions[newStatus] || `Status changed to ${newStatus}`,
        planned_date: toLocalDateString(new Date()),
        actual_date: toLocalDateString(new Date()),
        status: 'completed',
        is_task: false,
        notes: notes
      })

      // If transitioning to 'ready', complete the pending colonisation task
      if (newStatus === 'ready' && previousStatus === 'incubating') {
        await supabase
          .from('timeline_events')
          .update({
            status: 'completed',
            actual_date: toLocalDateString(new Date())
          })
          .eq('entity_type', 'liquid_culture')
          .eq('entity_id', id)
          .eq('event_type', 'colonisation_complete')
          .eq('status', 'pending')
      }

      toast.success(`${currentLC.culture_code} marked as ${newStatus}`)

      return data
    } catch (err) {
      console.error('[updateStatus] Error:', err)
      setError(err.message)
      toast.error('Failed to update status', {
        description: err.message
      })
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    createLC,
    updateLC,
    updateStatus,
    loading,
    error
  }
}

export default useLCMutations
